import type { Node, NodeProps } from '@xyflow/react';
import { memo } from 'react';
import { Card, CardContent } from '@savvy-kit/components/card';
import { Text } from '@savvy-kit/foundations/text';
import { cn } from '@savvy-kit/lib/utils';

import type { HouseholdNodeRenderData } from './types';
import { HouseholdAvatar } from './household-avatar';
import { CenteredHandles } from './node-handles';

const AUM_FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  maximumFractionDigits: 1,
});

export type HouseholdNode = Node<HouseholdNodeRenderData, 'household'>;

function HouseholdNodeCardComponent({ data }: NodeProps<HouseholdNode>) {
  const aum = data.savvyAum === null ? null : AUM_FORMATTER.format(Number(data.savvyAum));

  return (
    <Card
      className={cn(
        'w-56 cursor-pointer rounded-lg border-light p-0 transition-all duration-200',
        data.focused && 'border-primary ring-2 ring-primary',
        data.faded && 'opacity-30',
      )}
    >
      <CenteredHandles />
      <CardContent className="flex items-start gap-2.5 p-3">
        <HouseholdAvatar size="lg" />
        <div className="flex min-w-0 flex-col gap-0.5">
          <Text variant="subtitle-sm" color="dark" className="truncate">
            {data.name}
          </Text>
          <Text variant="caption" color="secondary" className="truncate">
            {aum ?? 'AUM unavailable'}
            {data.advisorName ? ` · ${data.advisorName}` : ''}
          </Text>
        </div>
      </CardContent>
    </Card>
  );
}

// The parent rebuilds `data` on every render (even unrelated drag frames), so a default
// shallow-props comparison would never skip a re-render — compare the fields that actually
// affect this card's output instead.
export const HouseholdNodeCard = memo(
  HouseholdNodeCardComponent,
  (prev, next) =>
    prev.data.name === next.data.name &&
    prev.data.savvyAum === next.data.savvyAum &&
    prev.data.advisorName === next.data.advisorName &&
    prev.data.faded === next.data.faded &&
    prev.data.focused === next.data.focused,
);
